/** @typedef {'TODAY'|'7D'|'30D'|'YTD'|'ALL'|'CUSTOM'} DashboardOrderPeriod */

export const ORDER_PERIOD_OPTIONS = [
  { id: 'TODAY', label: 'Today' },
  { id: '7D', label: '7d' },
  { id: '30D', label: '30d' },
  { id: 'YTD', label: 'YTD' },
  { id: 'CUSTOM', label: 'Custom' },
  { id: 'ALL', label: 'All time' },
];

export const BUCKET_OPTIONS = [
  { id: 'date', label: 'By day' },
  { id: 'weekday', label: 'By weekday' },
  { id: 'month', label: 'By month' },
  { id: 'hour', label: 'By hour' },
];

/** Which statuses to stack on the chart (business views). */
export const CHART_STATUS_VIEWS = [
  { id: 'ALL', label: 'All stages' },
  { id: 'PIPELINE', label: 'Production pipeline' },
  { id: 'POST', label: 'Post-production' },
  { id: 'FULFILMENT', label: 'Dispatch & delivery' },
  { id: 'EARLY', label: 'Pending & approved' },
];

const CHART_VIEW_KEYS = {
  PIPELINE: ['PLACE_ORDER', 'IN_PRODUCTION', 'PRODUCTION_DONE'],
  POST: ['BUTTA_CUTTING', 'MILL_FINISH', 'BORDER_CUTTING', 'HANDWORK'],
  FULFILMENT: ['READY_FOR_DISPATCH', 'IN_STOCK', 'DISPATCHED', 'DELIVERED'],
  EARLY: ['DRAFT', 'APPROVED', 'PLACE_ORDER'],
};

export function statusKeysForChartView(viewId, activeKeys) {
  const allowed = CHART_VIEW_KEYS[viewId];
  if (!allowed) return activeKeys;
  return activeKeys.filter((k) => allowed.includes(k));
}

/**
 * How the chart X-axis is grouped for each period filter.
 * TODAY → 24 hours; 7D/30D → one bar per calendar day; YTD/ALL → months.
 */
export function defaultBucketForPeriod(period) {
  switch (period) {
    case 'TODAY':
      return 'hour';
    case '7D':
    case '30D':
      return 'date';
    case 'YTD':
    case 'ALL':
      return 'month';
    default:
      return 'date';
  }
}

/** Sensible bucket choices per period (business UX). */
export function allowedBucketsForPeriod(period) {
  switch (period) {
    case 'TODAY':
      return BUCKET_OPTIONS.filter((b) => b.id === 'hour' || b.id === 'date');
    case '7D':
      return BUCKET_OPTIONS.filter((b) => ['date', 'weekday'].includes(b.id));
    case '30D':
      return BUCKET_OPTIONS.filter((b) => b.id === 'date' || b.id === 'weekday');
    case 'YTD':
    case 'ALL':
      return BUCKET_OPTIONS.filter((b) => b.id === 'month' || b.id === 'weekday');
    case 'CUSTOM':
      return BUCKET_OPTIONS;
    default:
      return BUCKET_OPTIONS;
  }
}

export function buildOrdersAnalyticsParams({ period, from, to, bucket }) {
  const params = new URLSearchParams();
  params.set('period', period || '30D');
  if (bucket) params.set('bucket', bucket);
  if (period === 'CUSTOM') {
    if (from) params.set('from', from);
    if (to) params.set('to', to);
  }
  return params.toString();
}

export function formatInr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return '₹0';
  if (n >= 1_000_000) return `₹${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `₹${(n / 1_000).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString()}`;
}

export function formatPeriodRange(analytics) {
  if (!analytics) return '';
  if (analytics.period === 'ALL') return 'All time';
  const from = analytics.fromDate;
  const to = analytics.toDate;
  if (!from && !to) return '';
  try {
    const fmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    if (from && to && from === to) return fmt.format(new Date(from));
    if (from && to) return `${fmt.format(new Date(from))} – ${fmt.format(new Date(to))}`;
    if (from) return `From ${fmt.format(new Date(from))}`;
    if (to) return `Until ${fmt.format(new Date(to))}`;
  } catch {
    return from && to ? `${from} – ${to}` : '';
  }
  return '';
}

/** Roll up chart buckets + status counts for KPI strip. */
export function computeAnalyticsTotals(analytics) {
  const buckets = analytics?.chartBuckets || [];
  let totalPieces = 0;
  let totalAmount = 0;
  for (const b of buckets) {
    totalPieces += Number(b.totalPieces) || 0;
    totalAmount += Number(b.totalAmount) || 0;
  }
  const totalOrders = Number(analytics?.orderTotalCount) || 0;
  const avgOrderValue = totalOrders > 0 ? totalAmount / totalOrders : 0;
  const avgPiecesPerOrder = totalOrders > 0 ? totalPieces / totalOrders : 0;
  return { totalOrders, totalPieces, totalAmount, avgOrderValue, avgPiecesPerOrder };
}
